/**
 * Argus Live — cockpit OPERATE route (`/argus-live/dashboards/:id`).
 *
 * The user-facing surface to OPEN and OPERATE a published cockpit. Coexists
 * with the editor route at `/argus-live/dashboards/:id/edit`:
 *   - `/argus-live/dashboards/$id`        → this OPERATE view (run the cockpit)
 *   - `/argus-live/dashboards/$id/edit`   → the 3-pane authoring editor
 *
 * Data loading + auth mirror the editor route:
 *   - Layer 1: `useHasPermission(`read`, `argus_live`)` hard-gates the page.
 *   - Layer 2: `dashboardData.effectiveRole` (per-dashboard grant) decides
 *     whether the user operates read-only (any non-null grant) or can execute
 *     gated actions (operator / edit / admin). The CockpitOperateView consumes
 *     these and the runtime host re-enforces them server-side.
 *
 * A user with no grant gets the same friendly 404/not-found state the editor
 * uses (the API returns 404 when the caller can't see the dashboard).
 */

import { createFileRoute } from '@tanstack/react-router';
import { Loader2, AlertCircle } from 'lucide-react';
import { Link } from '@tanstack/react-router';
import { useArgusLiveDashboard } from '@/lib/use-argus-live';
import { useHasPermission } from '@/lib/use-has-permission';
import { CockpitOperateView } from '@/components/argus-live';

export const Route = createFileRoute('/argus-live/dashboards/$id/')({
  component: ArgusLiveOperateRoute,
});

function ArgusLiveOperateRoute() {
  const { id } = Route.useParams();

  const canRead = useHasPermission('read', 'argus_live');
  const canWrite = useHasPermission('write', 'argus_live');

  const dashboardQuery = useArgusLiveDashboard(id, { enabled: canRead });

  // ── Layer 1 hard-gate ────────────────────────────────────────────────
  if (!canRead) {
    return (
      <div className="h-full flex flex-col items-center justify-center text-center p-6">
        <AlertCircle className="h-10 w-10 text-amber-500/70 mb-3" />
        <h2 className="text-base font-semibold mb-1">
          You don't have access to Argus Live
        </h2>
        <p className="text-sm text-muted-foreground max-w-md mb-4">
          Argus Live requires the{' '}
          <code className="font-mono">read:argus_live</code> permission. Ask a
          tenant admin to grant it via the Users module, or contact your platform
          administrator if you believe this is an error.
        </p>
        <Link
          to="/ask-argus"
          className="inline-flex items-center justify-center h-9 px-3 text-sm font-medium rounded-md border border-input bg-background hover:bg-accent hover:text-accent-foreground"
        >
          Back to Ask Argus
        </Link>
      </div>
    );
  }

  // ── Loading ──────────────────────────────────────────────────────────
  if (dashboardQuery.isLoading) {
    return (
      <div className="h-full flex flex-col items-center justify-center text-center p-6">
        <Loader2 className="h-6 w-6 text-primary animate-spin mb-3" />
        <p className="text-xs text-muted-foreground">Opening cockpit…</p>
      </div>
    );
  }

  // ── Error / 404 ──────────────────────────────────────────────────────
  if (dashboardQuery.error || !dashboardQuery.data?.data) {
    const status = (dashboardQuery.error as { status?: number } | null)?.status;
    const isNotFound = status === 404;
    return (
      <div className="h-full flex flex-col items-center justify-center text-center p-6">
        <AlertCircle className="h-10 w-10 text-destructive/70 mb-3" />
        <h2 className="text-base font-semibold mb-1">
          {isNotFound ? 'Cockpit not found' : 'Failed to open cockpit'}
        </h2>
        <p className="text-sm text-muted-foreground max-w-md mb-4">
          {isNotFound
            ? `No cockpit exists with id ${id}, or you don't have a grant on it.`
            : dashboardQuery.error?.message ?? 'Unknown error.'}
        </p>
        <Link
          to="/argus-live"
          className="inline-flex items-center justify-center h-9 px-3 text-sm font-medium rounded-md border border-input bg-background hover:bg-accent hover:text-accent-foreground"
        >
          Back to cockpits
        </Link>
      </div>
    );
  }

  return (
    <CockpitOperateView
      dashboardData={dashboardQuery.data.data}
      canWriteFeature={canWrite}
    />
  );
}
